import type { Alert, GameStats, PlayerDecision, RoundResult } from './types';

export interface ScoreWeights {
  correctEscalate: number;
  correctDismiss: number;
  correctInvestigate: number;
  correctTune: number;
  missedAttack: number;
  falseEscalation: number;
  overTuned: number;
  slowDecision: number;
  speedBonus: number;
  streakMultiplier: number;
}

export const DEFAULT_WEIGHTS: ScoreWeights = {
  correctEscalate: 100,
  correctDismiss: 50,
  correctInvestigate: 30,
  correctTune: 70,
  missedAttack: -200,
  falseEscalation: -50,
  overTuned: -150,
  slowDecision: -10,
  speedBonus: 1.5,
  streakMultiplier: 0.1,
};

export function calculatePoints(
  alert: Alert,
  decision: PlayerDecision,
  streak: number,
  weights: ScoreWeights = DEFAULT_WEIGHTS
): { points: number; isCorrect: boolean; breakdown: string[] } {
  const { action } = decision;
  const { correctAction } = alert;
  const isCorrect = action === correctAction;
  const breakdown: string[] = [];
  let points = 0;

  if (isCorrect) {
    switch (action) {
      case 'escalate':
        points = weights.correctEscalate;
        breakdown.push(`+${weights.correctEscalate} Correctly escalated real threat`);
        break;
      case 'dismiss':
        points = weights.correctDismiss;
        breakdown.push(`+${weights.correctDismiss} Correctly dismissed false positive`);
        break;
      case 'investigate':
        points = weights.correctInvestigate;
        breakdown.push(
          `+${weights.correctInvestigate} Appropriate caution - investigation warranted`
        );
        break;
      case 'tune':
        points = weights.correctTune;
        breakdown.push(`+${weights.correctTune} Correctly tuned recurring noise`);
        break;
    }

    // Speed bonus (under 10 seconds)
    if (decision.timeSpent < 10000) {
      const speedBonus = Math.round(points * (weights.speedBonus - 1));
      points += speedBonus;
      breakdown.push(`+${speedBonus} Speed bonus (<10s)`);
    }

    // Streak bonus
    if (streak >= 5) {
      const streakBonus = Math.round(points * (streak * weights.streakMultiplier));
      points += streakBonus;
      breakdown.push(`+${streakBonus} Streak bonus (${streak}x)`);
    }
  } else {
    // Wrong answer - determine penalty
    if (correctAction === 'escalate' && (action === 'dismiss' || action === 'tune')) {
      // Missed a real attack
      points = weights.missedAttack;
      breakdown.push(`${weights.missedAttack} MISSED ATTACK - should have escalated`);
    } else if (correctAction === 'dismiss' && action === 'escalate') {
      // False escalation
      points = weights.falseEscalation;
      breakdown.push(`${weights.falseEscalation} False escalation - wasted IR team time`);
    } else if (correctAction === 'tune' && action !== 'tune' && action !== 'dismiss') {
      // Should have tuned but escalated
      points = Math.round(weights.falseEscalation / 2);
      breakdown.push(`${points} Over-reacted to known noise`);
    } else if (
      action === 'tune' &&
      correctAction !== 'tune' &&
      correctAction !== 'dismiss'
    ) {
      // Tuned something that shouldn't be tuned
      points = weights.overTuned;
      breakdown.push(`${weights.overTuned} OVER-TUNED - will miss future attacks`);
    } else {
      // Partial credit cases
      points = -20;
      breakdown.push(`${points} Suboptimal decision`);
    }
  }

  // Slow decision penalty (over 30 seconds)
  if (decision.timeSpent > 30000) {
    points += weights.slowDecision;
    breakdown.push(`${weights.slowDecision} Slow decision (>30s)`);
  }

  return { points, isCorrect, breakdown };
}

export function evaluateDecision(
  alert: Alert,
  decision: PlayerDecision,
  streak: number
): RoundResult {
  const { points, isCorrect } = calculatePoints(alert, decision, streak);

  return {
    alertId: alert.id,
    playerAction: decision.action,
    correctAction: alert.correctAction,
    isCorrect,
    points,
    timeSpent: decision.timeSpent,
    explanation: alert.explanation,
  };
}

export function calculateStats(alerts: Alert[], results: RoundResult[]): GameStats {
  const totalAlerts = results.length;
  const correctCount = results.filter((r) => r.isCorrect).length;
  const incorrectCount = totalAlerts - correctCount;
  const totalPoints = results.reduce((sum, r) => sum + r.points, 0);

  // Calculate streak
  let streak = 0;
  let maxStreak = 0;
  for (const result of results) {
    if (result.isCorrect) {
      streak++;
      maxStreak = Math.max(maxStreak, streak);
    } else {
      streak = 0;
    }
  }

  // Precision: of alerts we escalated, how many were true positives?
  const escalations = results.filter((r) => r.playerAction === 'escalate');
  const trueEscalations = escalations.filter((r) => r.correctAction === 'escalate');
  const precision =
    escalations.length > 0 ? trueEscalations.length / escalations.length : 1;

  // Recall: of true attacks, how many did we escalate?
  const realAttacks = alerts.filter((a) => a.correctAction === 'escalate');
  const caughtAttacks = results.filter((r) => {
    const alert = alerts.find((a) => a.id === r.alertId);
    return alert?.correctAction === 'escalate' && r.playerAction === 'escalate';
  });
  const recall = realAttacks.length > 0 ? caughtAttacks.length / realAttacks.length : 1;

  // Missed attacks
  const missedAttacks = results.filter((r) => {
    const alert = alerts.find((a) => a.id === r.alertId);
    return alert?.correctAction === 'escalate' && r.playerAction !== 'escalate';
  }).length;

  // False escalations
  const falseEscalations = results.filter((r) => {
    const alert = alerts.find((a) => a.id === r.alertId);
    return alert?.correctAction !== 'escalate' && r.playerAction === 'escalate';
  }).length;

  // Average time
  const avgTimeMs =
    results.length > 0
      ? results.reduce((sum, r) => sum + r.timeSpent, 0) / results.length
      : 0;

  return {
    totalAlerts,
    correctCount,
    incorrectCount,
    totalPoints,
    streak,
    maxStreak,
    precision,
    recall,
    avgTimeMs,
    missedAttacks,
    falseEscalations,
  };
}

export function getGrade(stats: GameStats): {
  grade: string;
  label: string;
  color: string;
} {
  const accuracy = stats.totalAlerts > 0 ? stats.correctCount / stats.totalAlerts : 0;

  // Weighted score emphasizing recall (don't miss attacks) and accuracy
  const score = accuracy * 0.4 + stats.recall * 0.4 + stats.precision * 0.2;

  if (score >= 0.9 && stats.missedAttacks === 0) {
    return { grade: 'S', label: 'Elite Analyst', color: 'text-purple-500' };
  }
  if (score >= 0.8 && stats.missedAttacks <= 1) {
    return { grade: 'A', label: 'Senior SOC', color: 'text-emerald-500' };
  }
  if (score >= 0.7) {
    return { grade: 'B', label: 'Solid Analyst', color: 'text-blue-500' };
  }
  if (score >= 0.6) {
    return { grade: 'C', label: 'Learning', color: 'text-amber-500' };
  }
  if (score >= 0.5) {
    return { grade: 'D', label: 'Needs Practice', color: 'text-orange-500' };
  }
  return { grade: 'F', label: 'Alert Fatigue', color: 'text-rose-500' };
}
